import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Card,
  Group,
  Loader,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import {
  listItemUomsForMaintenance,
  setItemUomExcluded,
  type UomFactorSource,
} from './api';

const UOM_MAINT_KEY = 'item-uoms-maintenance';

interface Props {
  /** Null while creating — there is nothing generated until the item is saved. */
  itemId: string | null;
  canWrite: boolean;
}

/**
 * The units an item can be transacted in, as generated by the server.
 *
 * This exists because the two conversion inputs above it are only the SOURCE. The server chains
 * them and fans the result out through the tenant-wide physical conversions, so an item stocked
 * in CS with "1 CS = 1000 EA" and "1 EA = 10 LB" ends up transactable in EA, LB, and whatever
 * else those reach — KG, OZ, G, DZ. Typing two numbers and being shown eight units is
 * bewildering without this table, and the current product shows the equivalent grid.
 *
 * Factors are read-only. They are derived, recomputed on every item save, so an editable column
 * would be a second source of truth that loses its value silently on an unrelated edit.
 */
export function ItemUomTable({ itemId, canWrite }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const uoms = useQuery({
    queryKey: [UOM_MAINT_KEY, itemId ?? 'new'] as const,
    // Maintenance view: includes suppressed units, because hiding an exclusion would make it
    // impossible to undo. Never use this list to offer units to transact in.
    queryFn: ({ signal }) => listItemUomsForMaintenance(itemId!, signal),
    enabled: Boolean(itemId),
  });

  const toggle = useMutation({
    mutationFn: ({ uomId, isExcluded }: { uomId: string; isExcluded: boolean }) =>
      setItemUomExcluded(itemId!, uomId, isExcluded),
    // The response IS the new maintenance view, so seed the cache from it rather than refetching.
    onSuccess: (rows) => queryClient.setQueryData([UOM_MAINT_KEY, itemId ?? 'new'], rows),
  });

  // Nothing is generated until the item exists, and saying so beats an empty table that looks
  // like a failure.
  if (!itemId) {
    return (
      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        {t('items.uomTable.notYetSaved')}
      </Alert>
    );
  }

  if (uoms.isLoading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (uoms.isError) {
    // Named cause, not just "couldn't load". Every time this has actually fired the reason was a
    // tenant database missing a migration — this endpoint gained a parameter in script 030, and
    // an older database answers "too many arguments" — so the message points there and shows the
    // correlation id rather than leaving someone to guess.
    const correlationId =
      uoms.error instanceof ApiError ? uoms.error.problem.correlationId : undefined;
    return (
      <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">{t('items.uomTable.loadFailed')}</Text>
        {correlationId && (
          <Text size="xs" c="dimmed" mt={4}>
            {t('items.uomTable.reference', { id: correlationId })}
          </Text>
        )}
      </Alert>
    );
  }

  const rows = uoms.data ?? [];

  return (
    <Card withBorder padding={0}>
      {/* Stays a scroll container rather than moving to ResponsiveTable, for the same reason
          Planning does: reading ACROSS is the job here — unit, then its factor, then where that
          factor came from, then whether it is offered — and cards break the comparison. It is also
          capped at 280px inside a modal that already scrolls on a phone, so a stack of cards would
          nest one scroll region inside another. */}
      <Table.ScrollContainer minWidth={520} mah={280}>
        <Table striped highlightOnHover stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('items.uomTable.unit')}</Table.Th>
              <Table.Th ta="right">{t('items.uomTable.perStockingUnit')}</Table.Th>
              <Table.Th>{t('items.uomTable.source')}</Table.Th>
              <Table.Th ta="center">{t('items.uomTable.offered')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((u) => (
              <Table.Tr key={u.uomId} opacity={u.isExcluded ? 0.55 : 1}>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm" fw={u.isStockingUom ? 600 : 400}>
                      {u.uomCode}
                    </Text>
                    {u.isStockingUom && (
                      <Badge size="xs" variant="light">
                        {t('items.uomTable.stocking')}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {u.displayName}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" ff="monospace">
                    {formatFactor(u.factor)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {/* The whole point of the column: two inputs produce more units than were
                      typed, and without provenance an unexpected row has no explanation. */}
                  <Text size="xs" c="dimmed">
                    {sourceLabel(u.factorSource, t)}
                  </Text>
                </Table.Td>
                <Table.Td ta="center">
                  {/* The stocking unit is not togglable — inventory is held in it, and the
                      posting path reads this same list, so hiding it would surface as a failed
                      transaction rather than here. The server refuses it too. */}
                  <Tooltip
                    label={
                      u.isStockingUom
                        ? t('items.uomTable.stockingAlwaysOffered')
                        : canWrite
                          ? t('items.uomTable.offeredHint')
                          : t('items.uomTable.readOnly')
                    }
                  >
                    <Switch
                      size="sm"
                      checked={!u.isExcluded}
                      disabled={u.isStockingUom || !canWrite || toggle.isPending}
                      onChange={(e) =>
                        toggle.mutate({ uomId: u.uomId, isExcluded: !e.currentTarget.checked })
                      }
                      aria-label={t('items.uomTable.offeredAria', { unit: u.uomCode })}
                    />
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {toggle.isError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} m="sm">
          {toggle.error instanceof ApiError
            ? (toggle.error.problem.title ?? t('items.uomTable.toggleFailed'))
            : t('items.uomTable.toggleFailed')}
        </Alert>
      )}

      <Text size="xs" c="dimmed" p="sm">
        {t('items.uomTable.factorsDerived')}
      </Text>
    </Card>
  );
}

/**
 * Factors span a huge range — 1 for the stocking unit, 4,535,923.7 for grams per case — so a
 * fixed number of decimals either truncates the small ones or pads the large ones with noise.
 * Trailing zeros are dropped and the significant digits kept.
 */
function formatFactor(factor: number): string {
  return factor.toLocaleString(undefined, { maximumFractionDigits: 10 });
}

function sourceLabel(
  source: UomFactorSource | null,
  t: (key: string) => string,
): string {
  switch (source) {
    case 'stocking':
      return t('items.uomTable.sourceStocking');
    case 'conversion1':
      return t('items.uomTable.sourceConversion1');
    case 'conversion2':
      return t('items.uomTable.sourceConversion2');
    case 'global_from_stocking':
    case 'global_from_conversion1':
    case 'global_from_conversion2':
      // Collapsed to one label on purpose: which of the three chain steps a global conversion
      // hung off is server bookkeeping, not something an operator acts on. What matters is that
      // the factor came from the shared conversion table rather than from this item.
      return t('items.uomTable.sourceGlobal');
    default:
      // Rows generated before the column existed. Saving the item regenerates and fills it in.
      return t('items.uomTable.sourceUnknown');
  }
}
